if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.variantMediaMap = this.parseVariantMediaMap();
        this.syncingVariantFromMedia = false;

        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 150));
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset.mediaId;
        if (!mediaId) return;

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }

        if (!this.mql.matches) this.syncVariantFromMedia(mediaId);
      }

      parseVariantMediaMap() {
        try {
          return JSON.parse(this.querySelector('[data-variant-media-map]')?.textContent || '[]');
        } catch {
          return [];
        }
      }

      syncVariantFromMedia(mediaId) {
        if (this.syncingVariantFromMedia) return;

        const mapping = this.variantMediaMap.find((item) => item.mediaId === mediaId);
        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        if (!mapping || !variantSelects) return;

        try {
          const selectedVariant = JSON.parse(
            variantSelects.querySelector('[data-selected-variant]')?.textContent || 'null'
          );
          if (String(selectedVariant?.id) === String(mapping.variantId)) return;
        } catch {
          // Continue with option matching when selected variant data is unavailable.
        }

        let changeTarget = null;
        const optionGroups = variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown');

        optionGroups.forEach((group, index) => {
          const optionValue = String(mapping.options[index] ?? '');
          const input = Array.from(group.querySelectorAll('input[type="radio"]')).find(
            (candidate) => candidate.value === optionValue
          );
          const select = group.querySelector('select');

          if (input && !input.checked) {
            input.checked = true;
            changeTarget = input;
          } else if (select && select.value !== optionValue) {
            select.value = optionValue;
            select.querySelectorAll('option').forEach((option) => {
              option.toggleAttribute('selected', option.value === optionValue);
            });
            changeTarget = select;
          }
        });

        if (!changeTarget) return;

        this.syncingVariantFromMedia = true;
        changeTarget.dispatchEvent(new Event('change', { bubbles: true }));
        window.setTimeout(() => {
          this.syncingVariantFromMedia = false;
        }, 1000);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
